import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { administration, communication, developpement } from "../data/team";

type Person = {
    name: string;
    role: string;
    image: string;
    status?: "green" | "red";
    linkedin?: string;
    instagram?: string;
    mail?: string;
};

const SocialButton: React.FC<{
    href?: string;
    children: React.ReactNode;
}> = ({ href, children }) => {
    if (!href || href === "#") return null;

    const isMail = href.startsWith("mailto:");
    const props = isMail ? {} : { target: "_blank", rel: "noreferrer" };

    return (
        <a
            href={href}
            {...props}
            className="w-8 h-8 rounded-lg bg-white border border-slate-200 shadow-sm hover:bg-slate-50 flex items-center justify-center transition"
        >
            {children}
        </a>
    );
};

const TeamCard: React.FC<{ person: Person }> = ({ person }) => {
    const dotColor =
        person.status === "red"
            ? "bg-rose-500 shadow-[0_0_0_4px_rgba(244,63,94,0.15)]"
            : "bg-emerald-500 shadow-[0_0_0_4px_rgba(16,185,129,0.15)]";

    return (
        <div className="relative rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition p-4">
      <span
          className={`absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full ${dotColor}`}
      />

            <span
                className="absolute right-3 top-3 px-2 py-0.5
        text-[clamp(0.6rem,1.8vw,0.85rem)]
        font-semibold rounded-full
        bg-[#0B5ED7] text-white
        truncate max-w-[65%]"
            >
        #{person.role}
      </span>

            <div className="flex items-center gap-3 pl-6 pr-24">
                <img
                    src={person.image}
                    alt={person.name}
                    className="w-12 h-12 rounded-xl object-cover border border-slate-200"
                    loading="lazy"
                />

                <p className="font-semibold text-slate-900 truncate text-[clamp(0.85rem,2vw,1.1rem)]">
                    {person.name}
                </p>
            </div>

            <div className="absolute right-3 bottom-3 flex gap-2">
                <SocialButton href={person.linkedin}>
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-slate-700">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.852 3.369-1.852 3.601 0 4.267 2.37 4.267 5.455v6.288zM5.337 7.433a2.063 2.063 0 01-2.064-2.064 2.064 2.064 0 114.128 0c0 1.14-.925 2.064-2.064 2.064zM6.814 20.452H3.86V9h2.954v11.452z" />
                    </svg>
                </SocialButton>

                <SocialButton href={person.instagram}>
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-slate-700">
                        <path d="M7.75 2h8.5A5.75 5.75 0 0122 7.75v8.5A5.75 5.75 0 0116.25 22h-8.5A5.75 5.75 0 012 16.25v-8.5A5.75 5.75 0 017.75 2zm0 1.5A4.25 4.25 0 003.5 7.75v8.5A4.25 4.25 0 007.75 20.5h8.5a4.25 4.25 0 004.25-4.25v-8.5A4.25 4.25 0 0016.25 3.5h-8.5zM12 7a5 5 0 110 10 5 5 0 010-10zm0 1.5a3.5 3.5 0 100 7 3.5 3.5 0 000-7z" />
                    </svg>
                </SocialButton>

                <SocialButton href={person.mail}>
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-slate-700">
                        <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z" />
                    </svg>
                </SocialButton>
            </div>
        </div>
    );
};

const EmptyPoleCTA: React.FC<{ poleName: string }> = ({ poleName }) => {
    return (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center shadow-sm">
            <p className="text-sm font-semibold uppercase tracking-wide text-[#0B5ED7]">
                Recrutement ouvert
            </p>
            <h3 className="mt-2 text-xl font-bold text-slate-900">
                Le {poleName} cherche ses prochains bénévoles
            </h3>
            <Link
                to="/jobs"
                className="mt-5 inline-flex items-center justify-center gap-2 rounded-lg bg-[#0B5ED7] px-5 py-3 font-semibold text-white transition hover:bg-[#0139B7]"
            >
                Voir les postes ouverts
                <ArrowRight className="h-4 w-4" />
            </Link>
        </div>
    );
};

const TeamSection: React.FC<{
    title: string;
    poleName: string;
    members: Person[];
    gridClassName: string;
}> = ({ title, poleName, members, gridClassName }) => {
    const people = members.filter(Boolean);

    return (
        <section className="mb-12 last:mb-0">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
                {title}
            </h2>
            {people.length > 0 ? (
                <div className={gridClassName}>
                    {people.map((m, i) => (
                        <TeamCard key={`${m.name}-${i}`} person={m} />
                    ))}
                </div>
            ) : (
                <EmptyPoleCTA poleName={poleName} />
            )}
        </section>
    );
};

const TeamPage: React.FC = () => {
    return (
        <div className="pt-20 min-h-screen bg-slate-50">
            <div className="max-w-6xl mx-auto px-4 py-12">
                <div className="text-center mb-10">
                    <h1 className="text-4xl font-extrabold text-slate-900">
                        Notre Équipe
                    </h1>
                    <p className="text-slate-600 mt-3">
                        Découvrez les personnes qui font vivre FRANCE STUDENT.
                    </p>
                </div>

                <TeamSection
                    title="Administration"
                    poleName="pôle Administration"
                    members={administration as Person[]}
                    gridClassName="grid grid-cols-1 sm:grid-cols-2 gap-4"
                />

                <TeamSection
                    title="Pôle Communication"
                    poleName="pôle Communication"
                    members={communication as Person[]}
                    gridClassName="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4"
                />

                <TeamSection
                    title="Pôle Développement"
                    poleName="pôle Développement"
                    members={developpement as Person[]}
                    gridClassName="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4"
                />
            </div>
        </div>
    );
};

export default TeamPage;
